import { Building, Users } from "lucide-react";
import { useState, useEffect } from "react";

const CompanyModal = ({
  styles,
  t,
  isRTL,
  theme,
  detectedCompanies,
  detectingCompanies,
  showCompanyModal,
  setShowCompanyModal,
  handleSelectCompany,
  handleDetectCompanies,
  // Admin-level feature gate (registry: configurations.autoCreateConfigs).
  // Defaults to true so other call sites keep their behavior.
  canAutoCreate = true,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [hoveredButton, setHoveredButton] = useState(null);

  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === "Escape" && showCompanyModal) {
        setShowCompanyModal(false);
      }
    };

    window.addEventListener("keydown", handleEscKey);
    return () => window.removeEventListener("keydown", handleEscKey);
  }, [showCompanyModal, setShowCompanyModal]);

  if (!showCompanyModal) return null;

  const handleBackdropClick = (e) => {
    // Only close if clicking directly on the overlay, not on the modal content
    if (e.target === e.currentTarget) {
      setShowCompanyModal(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={handleBackdropClick}>
      <div style={{...styles.modal, scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch"}}>
        <button
          onClick={() => setShowCompanyModal(false)}
          onMouseEnter={() => setHoveredButton("close")}
          onMouseLeave={() => setHoveredButton(null)}
          style={{
            ...styles.closeButton,
            background: hoveredButton === "close" 
              ? (theme === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)")
              : "none",
            color: hoveredButton === "close"
              ? (theme === "dark" ? "#ffffff" : "#0f172a")
              : (theme === "dark" ? "rgba(255,255,255,0.75)" : "rgba(15,23,42,0.75)"),
          }}
          aria-label="Close modal"
        >
          ×
        </button>

        <h2 style={styles.modalTitle}>
          {t.companyModal.title}
          {!isRTL && <Building size={32} />}
          {isRTL && <Building size={32} />}
        </h2>
        <p style={styles.modalSubtitle}>{t.companyModal.subtitle}</p>

        {detectedCompanies.length === 0 ? (
          <p
            style={{
              color:
                theme === "dark"
                  ? "rgba(255,255,255,0.6)"
                  : "rgba(15,23,42,0.7)",
              textAlign: isRTL ? "right" : "left",
              fontSize: "clamp(14px, 2vw, 16px)",
            }}
          >
            {t.companyModal.noCompanies}
          </p>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: "16px",
            marginBottom: "24px",
          }}>
            {detectedCompanies.map((company, index) => (
              <div
                key={index}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{
                  ...styles.companyCard(company.isConfigured),
                  transform: hoveredIndex === index ? "translateY(-2px)" : "translateY(0)",
                  boxShadow: "none",
                }}
              >
                <div style={styles.companyName}>
                  {company.companyName}
                  {!isRTL && <Building size={18} />}
                  {isRTL && <Building size={18} />}
                </div>
                <div style={styles.companyDetails}>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      flexDirection: isRTL ? "row" : "row",
                    }}
                  >
                    <Users size={14} />
                    {company.employeeCount || 0}{" "}
                    {t.companyModal.employeeCount}
                  </span>
                  <div style={styles.badge(company.isConfigured)}>
                    {company.isConfigured
                      ? t.companyModal.configured
                      : t.companyModal.notConfigured}
                  </div>
                </div>
                <button
                  onClick={() => handleSelectCompany(company.companyName)}
                  onMouseEnter={() => setHoveredButton(`select-${index}`)}
                  onMouseLeave={() => setHoveredButton(null)}
                  style={{
                    ...styles.selectCompanyButton,
                    transform: hoveredButton === `select-${index}` ? "scale(1.02)" : "scale(1)",
                    boxShadow: "none",
                    opacity: hoveredButton === `select-${index}` ? 0.9 : 1,
                  }}
                >
                  {t.companyModal.selectCompany}
                </button>
              </div>
            ))}
          </div>
        )}

        {canAutoCreate && (
          <button
            onClick={() => handleDetectCompanies(true)}
            onMouseEnter={() => setHoveredButton("auto")}
            onMouseLeave={() => setHoveredButton(null)}
            style={{
              ...styles.autoCreateButton,
              marginTop: "24px",
              transform: hoveredButton === "auto" ? "scale(1.02)" : "scale(1)",
              boxShadow: "none",
              opacity: hoveredButton === "auto" ? 0.9 : 1,
            }}
            disabled={detectingCompanies}
          >
            {t.companyModal.autoCreate}
          </button>
        )}
      </div>
    </div>
  );
};

export default CompanyModal;